import {
  Box,
  InputAdornment,
  InputBaseComponentProps,
  SxProps,
  TextField,
} from '@mui/material';
import { Controller, Path } from 'react-hook-form';
import { BaseControlledInputProps } from '../basicControlledInputProps.types';
import FormFieldWrappers from '../FormFieldWrappers';
import './ControlledTextField.scss';
import PhoneNumberMask from './PhoneNumberMask';
import { useTranslation } from 'react-i18next';
import { useFormTracking } from '../hooks/index';

export interface TextFieldProps {
  /**
   * Whether the text field is multiline
   */
  multiline?: boolean;
  /**
   * Minimum number of rows for the multiline text
   * field
   */
  minRows?: number;
  /**
   * Maximum number of rows for the multiline text
   * field
   */
  maxRows?: number;
  /**
   * The character limit for the text field
   */
  characterLimit?: number;
  /**
   * Whether to hide the character count
   */
  hideCharacterCount?: boolean;
  /**
   * Whether the text field is a phone number
   */
  isPhoneNumber?: boolean;
  /**
   * Whether the text field is a number
   */
  isNumber?: boolean;
  /**
   * Can have adornments appear above and below input instead
   */
  verticalAdornments?: boolean;
  /**
   * The placeholder text for the text field
   */
  placeholder?: string;
  /**
   * The variant of the text field
   */
  variant?: 'outlined' | 'standard' | 'filled';
  /**
   * Style overrides
   */
  sx?: SxProps;
}

export type ControlledTextFieldProps<
  FORM_TYPE,
  FIELD_NAME extends Path<FORM_TYPE>,
> = BaseControlledInputProps<FORM_TYPE, FIELD_NAME> & TextFieldProps;

export const ControlledTextField = <
  FORM_TYPE,
  FIELD_NAME extends Path<FORM_TYPE>,
>({
  name,
  label,
  defaultValue = undefined,
  required,
  rules,
  multiline = false,
  placeholder,
  minRows = 3,
  maxRows = 3,
  characterLimit = 0,
  disabled = false,
  isPhoneNumber = false,
  isNumber = false,
  iconAdornment = null,
  control,
  onBlurCallback,
  onFocusCallback,
  verticalAdornments = false,
  hidden = false,
  trackingName,
  sensitive = true,
  variant = 'outlined',
  sx = {},
}: ControlledTextFieldProps<FORM_TYPE, FIELD_NAME>) => {
  const { t } = useTranslation();

  const trackingProps = useFormTracking(trackingName, sensitive);

  if (verticalAdornments && isPhoneNumber) {
    throw Error(
      'Vertical adornments are not supported for phone number fields for aesthetic reasons.'
    );
  }
  if (verticalAdornments && !multiline) {
    throw Error(
      'Vertical adornments should only be used for multiline fields for aesthetic reasons.'
    );
  }

  if (isPhoneNumber && isNumber) {
    throw Error('Text field cannot be of type PhoneNumber and Number.');
  }

  let type = 'text';
  if (isPhoneNumber) {
    type = 'tel';
  } else if (isNumber) {
    type = 'number';
  }
  let startAdornment: JSX.Element = null;
  if (isPhoneNumber) {
    startAdornment = <InputAdornment position="start">+1</InputAdornment>;
  }
  let endAdornment: JSX.Element = null;
  if (iconAdornment) {
    if (verticalAdornments) {
      const margin = multiline ? '12px 0px 0px 0px' : '-4px 0px 12px 0px';
      endAdornment = <Box sx={{ margin, width: '100%' }}>{iconAdornment}</Box>;
    } else {
      endAdornment = (
        <InputAdornment position="end">{iconAdornment}</InputAdornment>
      );
    }
  }

  const controllerRules = {
    ...(required && { required: `${label} is required.` }),
    ...(isPhoneNumber && {
      maxLength: {
        value: 12,
        message: 'Phone number requires 10 digits.',
      },
      minLength: {
        value: 12,
        message: 'Phone number requires 10 digits. too few',
      },
    }),
    ...rules,
  };

  return (
    <FormFieldWrappers.FieldWrapper>
      <FormFieldWrappers.InputWrapper>
        <Controller
          control={control}
          name={name}
          defaultValue={defaultValue}
          rules={controllerRules}
          render={({ field, fieldState: { error } }) =>
            !hidden && (
              <TextField
                {...field}
                {...trackingProps}
                id={`${name}-input`}
                disabled={disabled}
                placeholder={placeholder}
                label={label}
                type={type}
                multiline={multiline}
                minRows={minRows}
                maxRows={maxRows}
                fullWidth
                variant={variant}
                onChange={field.onChange}
                onBlur={e => {
                  if (onBlurCallback) {
                    onBlurCallback(e, field.onChange);
                  } else {
                    field.onChange(e);
                  }
                }}
                onFocus={e => {
                  if (onFocusCallback) {
                    onFocusCallback(e, field.onChange);
                  } else {
                    field.onChange(e);
                  }
                }}
                inputProps={{
                  ...(characterLimit > 0 && { maxLength: characterLimit }),
                }}
                InputProps={{
                  startAdornment,
                  endAdornment,
                  ...(isPhoneNumber && {
                    inputComponent:
                      PhoneNumberMask as unknown as React.ElementType<InputBaseComponentProps>,
                  }),
                  slotProps: {
                    ...(!!verticalAdornments && {
                      root: { className: 'input-vertical-adornment' },
                    }),
                  },
                }}
                helperText={t(error?.message)}
                error={!!error}
                sx={{
                  backgroundColor: 'white',
                  borderRadius: '16px',
                  ...sx,
                }}
              />
            )
          }
        />
      </FormFieldWrappers.InputWrapper>
    </FormFieldWrappers.FieldWrapper>
  );
};
